import { useEffect, useState } from 'react';
import { getGameConfig, getGameDisplayName, GAME_CONFIG } from './games';
import { safeLocalStorageSetItem } from './utils/safeStorage';

// Remove accents from a string using Unicode normalization
export const removeAccents = (str) => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};


// More efficient cleanup function that handles all games at once
// This should be called infrequently (e.g., on app mount) rather than on every save operation
export const cleanAllOldGameData = (daysAgo = 30) => {
  const now = new Date();
  const cutoffDate = new Date();
  cutoffDate.setDate(now.getDate() - daysAgo);

  // Use game ids from GAME_CONFIG
  const games = Object.values(GAME_CONFIG).map(g => g.id);
  let cleanedCount = 0;

  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    
    // Check if the key matches any game pattern
    const isGameKey = games.some(game => key.startsWith(`${game}_`));
    
    if (isGameKey) {
      const datePart = key.split("_")[1]; // Extract the date part from the key
      
      // Parse the date and compare
      const keyDate = new Date(datePart);
      if (keyDate < cutoffDate) {
        console.log(`Removing old localStorage key: ${key}`);
        localStorage.removeItem(key);
        cleanedCount++;
      }
    }
  }
  
  if (cleanedCount > 0) {
    console.log(`Cleaned ${cleanedCount} old game data entries`);
  }
  
  return cleanedCount;
};

export const getLocalStorageKey = (game, date) => {
  return `${game}_${date}`;
};

export const getLocalDataByKey = (key) => {
  if (!key) return {};
  try {
    return JSON.parse(localStorage.getItem(key)) || {};
  } catch (_) {
    return {};
  }
};

export const getLocalData = (game, date, options = {}) => {
  const key = options.storageKey || getLocalStorageKey(game, date);
  return getLocalDataByKey(key);
};

const isSerpentoniFinished = (gameData = {}) => {
  const status = gameData.status;
  const lives = typeof gameData.lives === 'number' ? gameData.lives : null;

  return (
    gameData.player_won === true ||
    gameData.playerWon === true ||
    gameData.game_over === true ||
    gameData.gameOver === true ||
    status === 'won' ||
    status === 'lost' ||
    (lives !== null && lives <= 0)
  );
};

const hasSerpentoniProgress = (gameData = {}) => {
  const foundWordIndices = gameData.found_word_indices || gameData.foundWordIndices || [];
  const spangramFound = gameData.spangram_found === true || gameData.spangramFound === true;
  const lives = typeof gameData.lives === 'number' ? gameData.lives : null;
  const status = gameData.status;

  return (
    isSerpentoniFinished(gameData) ||
    (typeof gameData.seq === 'number' && gameData.seq > 0) ||
    (Array.isArray(foundWordIndices) && foundWordIndices.length > 0) ||
    spangramFound ||
    (lives !== null && lives < 10) ||
    status === 'in_progress'
  );
};

export const setLocalDataByKey = (key, data) => {
  if (!key) return;
  localStorage.setItem(key, JSON.stringify(data));
};

export const setLocalData = (game, date, data, options = {}) => {
  const key = options.storageKey || getLocalStorageKey(game, date);
  setLocalDataByKey(key, data);
};

export const getCurrentDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const day = String(today.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

// Format a Date as YYYY-MM-DD using local time (avoids UTC shifts)
const formatLocalDate = (dateObj) => {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Function to calculate the previous day's date
export const getPreviousDate = (currentDate) => {
  const date = new Date(currentDate);
  date.setDate(date.getDate() - 1);
  return date.toISOString().split("T")[0];
};

export const hasGameHistory = (game) => {
  // Check all localStorage keys for this game
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key.startsWith(`${game}_`)) {
      return true;
    }
  }
  return false;
};

export const getRecommendedGame = (currentGame, currentDate, getServerStatus = null) => {
  // Build visible games sorted by order
  const visibleGamesSorted = Object.values(GAME_CONFIG)
    .filter(g => !g.hidden)
    .sort((a, b) => a.order - b.order);

  const gameOrder = visibleGamesSorted.map(g => g.id);
  
  // Helper to get game status for a specific game and date
  const getGameStatusForDate = (game, date) => {
    // First, check server status if available (more reliable for logged-in users)
    if (getServerStatus) {
      const serverStatus = getServerStatus(game, date);
      if (serverStatus) {
        return serverStatus; // 'not_started', 'in_progress', or 'finished'
      }
    }
    
    // Fall back to local storage check
    const gameData = getLocalData(game, date);
    if (!gameData || Object.keys(gameData).length === 0) {
      return 'not_started';
    }
    
    // Check if game is finished based on local data
    let isFinished = false;
    switch (game) {
      case 'ortografia':
        isFinished =
          gameData.status === 'won' ||
          gameData.playerWon === true ||
          gameData.gameOver === true;
        break;
      case 'memoria':
        isFinished = gameData.gameOver === true || gameData.matched?.length === 12;
        break;
      case 'collegamenti':
        isFinished = gameData.gameOver === true || gameData.foundGroups?.length === 4;
        break;
      case 'scalinata':
        isFinished = gameData.gameOver === true;
        break;
      case 'cloze':
        isFinished = gameData.gameOver === true || gameData.completed === true;
        break;
      case 'spettri':
        isFinished = gameData.gameOver === true || gameData.completed === true;
        break;
      case 'serpentoni':
        isFinished = isSerpentoniFinished(gameData);
        break;
      default:
        isFinished = false;
    }
    
    if (isFinished) {
      return 'finished';
    }
    
    // Check if game has been started (has any progress)
    let hasStarted = false;
    switch (game) {
      case 'ortografia':
        hasStarted = gameData.score > 0 || gameData.submittedWords?.length > 0;
        break;
      case 'memoria':
        hasStarted = gameData.matched?.length > 0;
        break;
      case 'collegamenti':
        hasStarted = gameData.foundGroups?.length > 0 || (typeof gameData.lives === 'number' && gameData.lives < 5);
        break;
      case 'scalinata':
        hasStarted = Object.keys(gameData.guesses || {}).length > 0;
        break;
      case 'cloze':
        hasStarted = gameData.placements?.some(x => x !== null);
        break;
      case 'spettri':
        hasStarted = Object.keys(gameData).length > 0;
        break;
      case 'serpentoni':
        hasStarted = hasSerpentoniProgress(gameData);
        break;
      default:
        hasStarted = Object.keys(gameData).length > 0;
    }
    
    return hasStarted ? 'in_progress' : 'not_started';
  };
  
  // Helper to build recommendation result
  const buildRecommendation = (game, date) => {
    const gameConfig = getGameConfig(game);
    const pathName = gameConfig ? gameConfig.pathName : game;
    return {
      type: 'game',
      game: game,
      path: `/${pathName}/${date}`,
      text: `Gioca ${getGameDisplayName(game)}`
    };
  };
  
  // Check games for the current date
  const notStartedGames = [];
  const inProgressGames = [];
  
  gameOrder.forEach(game => {
    if (game === currentGame) return;
    const status = getGameStatusForDate(game, currentDate);
    if (status === 'not_started') {
      notStartedGames.push(game);
    } else if (status === 'in_progress') {
      inProgressGames.push(game);
    }
  });
  
  // Priority 1: First in_progress game
  if (inProgressGames.length > 0) {
    return buildRecommendation(inProgressGames[0], currentDate);
  }
  
  // Priority 2: First not_started game
  if (notStartedGames.length > 0) {
    return buildRecommendation(notStartedGames[0], currentDate);
  }
  
  // Priority 3: Look at previous days for incomplete games
  // Generate previous dates (up to 59 days back, 60 total with current date)
  const previousDates = [];
  const today = new Date(currentDate + 'T12:00:00'); // Use noon to avoid timezone issues
  for (let i = 1; i <= 59; i++) {
    const prevDate = new Date(today);
    prevDate.setDate(today.getDate() - i);
    const year = prevDate.getFullYear();
    const month = String(prevDate.getMonth() + 1).padStart(2, '0');
    const day = String(prevDate.getDate()).padStart(2, '0');
    previousDates.push(`${year}-${month}-${day}`);
  }
  
  // Check previous days for not_started or in_progress games
  for (const date of previousDates) {
    for (const game of gameOrder) {
      const status = getGameStatusForDate(game, date);
      if (status === 'not_started' || status === 'in_progress') {
        return buildRecommendation(game, date);
      }
    }
  }
  
  // All games completed - return home fallback
  // Never recommend completed games
  return {
    type: 'home',
    path: '/',
    text: 'Torna alla home'
  };
};

/**
 * Get a random unstarted game from the last 60 days.
 *
 * Selection rules:
 * All players can pick from unstarted game+date pairs (today + archive).
 * Gameplay eligibility is checked centrally when the selected puzzle opens.
 *
 * @param {string} currentDate - The current date in YYYY-MM-DD format
 * @param {function} getServerStatus - Optional function to get server status (game, date) => status
 * @param {object} options - Retained for compatibility with existing callers
 * @returns {object|null} - { type: 'game', game, path, text, date, requiresAuth } or null if no unstarted games
 */
export const getRandomUnstartedGame = (currentDate, getServerStatus = null, options = {}) => {
  // Get all visible games on the platform
  const visibleGames = Object.values(GAME_CONFIG).filter(g => !g.hidden);
  
  // Generate dates: today + 59 previous days (60 total)
  const dates = [currentDate];
  const today = new Date(currentDate + 'T12:00:00');
  for (let i = 1; i <= 59; i++) {
    const prevDate = new Date(today);
    prevDate.setDate(today.getDate() - i);
    const year = prevDate.getFullYear();
    const month = String(prevDate.getMonth() + 1).padStart(2, '0');
    const day = String(prevDate.getDate()).padStart(2, '0');
    dates.push(`${year}-${month}-${day}`);
  }
  
  // Build list of all unstarted game+date combinations
  const unstartedGameDatePairs = [];
  
  for (const date of dates) {
    for (const game of visibleGames) {
      // Check server status first, fall back to local storage check
      let status = null;
      if (getServerStatus) {
        status = getServerStatus(game.id, date);
      }
      if (!status) {
        // Fall back to local storage check
        const gameData = getLocalData(game.id, date);
        status = (!gameData || Object.keys(gameData).length === 0) ? 'not_started' : 'started';
      }
      
      if (status === 'not_started') {
        unstartedGameDatePairs.push({ game, date });
      }
    }
  }
  
  if (unstartedGameDatePairs.length === 0) return null;
  
  // All players can explore released puzzles, including archive dates.
  const randomPair = unstartedGameDatePairs[Math.floor(Math.random() * unstartedGameDatePairs.length)];
  return {
    type: 'game',
    game: randomPair.game.id,
    date: randomPair.date,
    path: `/${randomPair.game.pathName}/${randomPair.date}`,
    text: `Gioca ${randomPair.game.displayName}`,
    requiresAuth: false,
  };
};

// Determine if a given game has been played on a specific date
export const hasPlayedGameOnDate = (game, date) => {
  const gameData = getLocalData(game, date);
  if (!gameData || Object.keys(gameData).length === 0) return false;
  switch (game) {
    case 'ortografia':
      return gameData.score > 0 || (gameData.submittedWords && gameData.submittedWords.length > 0);
    case 'memoria':
      return gameData.gameOver === true || (gameData.matched && gameData.matched.length > 0);
    case 'collegamenti':
      return gameData.gameOver === true || (gameData.foundGroups && gameData.foundGroups.length > 0);
    case 'scalinata': {
      const guesses = gameData.guesses || {};
      return Object.keys(guesses).some(level => Array.isArray(guesses[level]) && guesses[level].length > 0) || gameData.gameOver === true;
    }
    case 'cloze':
      return gameData.gameOver === true || gameData.completed === true;
    case 'spettri':
      return gameData.gameOver === true || gameData.completed === true;
    case 'serpentoni':
      return hasSerpentoniProgress(gameData);
    default:
      return Object.keys(gameData).length > 0;
  }
};

// Count how many distinct games were played on a specific date
export const getGamesPlayedCountOnDate = (date) => {
  const games = Object.values(GAME_CONFIG).map(g => g.id);
  let count = 0;
  for (const game of games) {
    if (hasPlayedGameOnDate(game, date)) count += 1;
  }
  return count;
};

// Check whether a specific game/date has an explicit gameStartTime (used for 24h gating)
export const hasStartedGameSession = (game, date) => {
  const data = getLocalData(game, date);
  if (!data) return false;
  if (typeof data.gameStartTime === 'number') return true;
  if (typeof data.gameStartTime === 'string') {
    const d = new Date(data.gameStartTime);
    return !isNaN(d.getTime());
  }
  return false;
};

// Count distinct game sessions (game+date) started within the past 24 hours
export const getGamesStartedCountInLast24Hours = () => {
  const games = Object.values(GAME_CONFIG).map(g => g.id);
  const cutoff = Date.now() - 24 * 60 * 60 * 1000;
  let count = 0;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    const matchingGame = games.find(g => key.startsWith(`${g}_`));
    if (!matchingGame) continue;
    try {
      const parsed = JSON.parse(localStorage.getItem(key));
      if (parsed && parsed.gameStartTime != null) {
        let ts = null;
        if (typeof parsed.gameStartTime === 'number') ts = parsed.gameStartTime;
        else if (typeof parsed.gameStartTime === 'string') {
          const d = new Date(parsed.gameStartTime);
          if (!isNaN(d.getTime())) ts = d.getTime();
        }
        if (ts != null && ts >= cutoff) {
          count += 1;
        }
      }
    } catch (_) {}
  }
  return count;
};

// Determine whether a given saved game data object reflects meaningful progress
export const hasMeaningfulProgress = (game, gameData) => {
  if (!gameData || Object.keys(gameData).length === 0) return false;
  switch (game) {
    case 'ortografia':
      return gameData.score > 0 || (Array.isArray(gameData.submittedWords) && gameData.submittedWords.length > 0);
    case 'memoria':
      return gameData.gameOver === true || (Array.isArray(gameData.matched) && gameData.matched.length > 0);
    case 'collegamenti':
      return gameData.gameOver === true ||
        (Array.isArray(gameData.foundGroups) && gameData.foundGroups.length > 0) ||
        (typeof gameData.lives === 'number' && gameData.lives < 5);
    case 'scalinata': {
      const guesses = gameData.guesses || {};
      const anyGuesses = Object.keys(guesses).some(level => Array.isArray(guesses[level]) && guesses[level].length > 0);
      return anyGuesses || gameData.gameOver === true;
    }
    case 'cloze':
      return gameData.gameOver === true || gameData.completed === true ||
        (Array.isArray(gameData.placements) && gameData.placements.some(x => x !== null));
    case 'spettri':
      return gameData.gameOver === true || gameData.completed === true;
    case 'serpentoni':
      return hasSerpentoniProgress(gameData);
    default:
      return Object.keys(gameData).length > 0;
  }
};

// Count distinct games with meaningful progress started since midnight today
export const getMeaningfulGamesStartedCountInLast24Hours = () => {
  const games = Object.values(GAME_CONFIG).map(g => g.id);
  // Calculate midnight of today in local time
  const today = new Date();
  const midnight = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
  const cutoff = midnight.getTime();
  let count = 0;
  const seenKeys = new Set();
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || seenKeys.has(key)) continue;
    const matchingGame = games.find(g => key.startsWith(`${g}_`));
    if (!matchingGame) continue;
    try {
      const parsed = JSON.parse(localStorage.getItem(key));
      if (!parsed) continue;
      let ts = null;
      
      // Check for created_at field (new format: ISO timestamp string)
      if (parsed.created_at != null && typeof parsed.created_at === 'string') {
        const d = new Date(parsed.created_at);
        if (!isNaN(d.getTime())) ts = d.getTime();
      }
      
      // Fall back to gameStartTime for backwards compatibility
      if (ts == null && parsed.gameStartTime != null) {
        if (typeof parsed.gameStartTime === 'number') {
          ts = parsed.gameStartTime;
        } else if (typeof parsed.gameStartTime === 'string') {
          const d = new Date(parsed.gameStartTime);
          if (!isNaN(d.getTime())) ts = d.getTime();
        }
      }
      
      // Check for meaningful progress: seq > 0
      const hasProgress = typeof parsed.seq === 'number' && parsed.seq > 0;
      
      if (ts != null && ts >= cutoff && hasProgress) {
        count += 1;
      }
    } catch (_) {}
    seenKeys.add(key);
  }
  return count;
};

export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const listener = () => setMatches(media.matches);
    
    // Set initial value if it doesn't match
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [query]); // Remove 'matches' from dependency array
  return matches;
}

export function toDateFormat(someDay) {
  const yyyy = someDay.getFullYear();
  const mm = String(someDay.getMonth() + 1).padStart(2, '0');
  const dd = String(someDay.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// Game status constants
export const GAME_STATUS = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress', 
  FINISHED: 'finished'
};

// Determine the status of a game for a specific date
export const getGameStatus = (game, date) => {
  const gameData = getLocalData(game, date);
  
  // If no data exists, game hasn't been started
  if (!gameData || Object.keys(gameData).length === 0) {
    return GAME_STATUS.NOT_STARTED;
  }
  
  // Check completion based on game type
  switch (game) {
    case 'ortografia':
      // Ortografia is finished when player reaches their chosen difficulty level
      const isFinished =
        gameData.status === 'won' ||
        gameData.playerWon === true ||
        gameData.gameOver === true;
      return isFinished ? GAME_STATUS.FINISHED : GAME_STATUS.IN_PROGRESS;
      
    case 'memoria':
      // Memoria is finished when all pairs are matched (12 cards = 6 pairs)
      const allMatched = gameData.matched?.length === 12;
      const gameOver = gameData.gameOver;
      return (allMatched || gameOver) ? GAME_STATUS.FINISHED : GAME_STATUS.IN_PROGRESS;
      
    case 'collegamenti':
      // Collegamenti is finished when all 4 groups are found
      const allGroupsFound = gameData.foundGroups?.length === 4;
      const collegamentiGameOver = gameData.gameOver;
      return (allGroupsFound || collegamentiGameOver) ? GAME_STATUS.FINISHED : GAME_STATUS.IN_PROGRESS;
      
    case 'scalinata': {
      const guesses = gameData.guesses || {};
      const MAX_GUESSES = 20;
      const answerLevels = Array.isArray(gameData.answers) ? gameData.answers.length : 0;
      const guessLevels = Object.keys(guesses).map(Number).filter(Number.isFinite);
      const finalLevel = Math.max(0, answerLevels - 1, ...guessLevels);
      const allLevelsCompleted = guesses[finalLevel] && guesses[finalLevel].length > 0 &&
        guesses[finalLevel][guesses[finalLevel].length - 1]?.feedback?.every(({ state }) => state === "correct");
      // Loss: used all guesses and not all levels completed
      const totalGuesses = Object.values(guesses).reduce((sum, arr) => sum + arr.length, 0);
      const outOfGuesses = totalGuesses >= MAX_GUESSES && !allLevelsCompleted;
      const scalinataGameOver = gameData.gameOver;
      return (allLevelsCompleted || outOfGuesses || scalinataGameOver) ? GAME_STATUS.FINISHED : GAME_STATUS.IN_PROGRESS;
    }

    case 'cloze': {
      // Cloze is finished when all blanks are correctly filled (completed) or user ran out of lives (gameOver)
      const completed = gameData.completed;
      const gameOver = gameData.gameOver;
      return (completed || gameOver) ? GAME_STATUS.FINISHED : GAME_STATUS.IN_PROGRESS;
    }
    
    case 'spettri':
      // Spettri is finished when game is completed or game over
      const spettriCompleted = gameData.completed === true;
      const spettriGameOver = gameData.gameOver === true;
      return (spettriCompleted || spettriGameOver) ? GAME_STATUS.FINISHED : GAME_STATUS.IN_PROGRESS;
    case 'serpentoni':
      if (isSerpentoniFinished(gameData)) {
        return GAME_STATUS.FINISHED;
      }
      return hasSerpentoniProgress(gameData) ? GAME_STATUS.IN_PROGRESS : GAME_STATUS.NOT_STARTED;
      
    default:
      return GAME_STATUS.NOT_STARTED;
  }
};

// Get status text for display (descriptive label)
export const getStatusText = (status) => {
  switch (status) {
    case GAME_STATUS.NOT_STARTED:
      return 'Non iniziato';
    case GAME_STATUS.IN_PROGRESS:
      return 'In corso';
    case GAME_STATUS.FINISHED:
      return 'Completato';
    default:
      return '';
  }
};

// Get action button text based on game status (call-to-action)
export const getActionButtonText = (status) => {
  switch (status) {
    case GAME_STATUS.NOT_STARTED:
      return 'Gioca';
    case GAME_STATUS.IN_PROGRESS:
      return 'Continua';
    case GAME_STATUS.FINISHED:
      return 'Rivedi';
    default:
      return 'Gioca';
  }
};

// Get encouraging message for today's game based on status
export const getTodayEncouragement = (game, status) => {
  switch (status) {
    case GAME_STATUS.NOT_STARTED:
      switch (game) {
        case 'collegamenti':
          return "Trova i quattro gruppi di quattro parole.";
        case 'scalinata':
          return "Scopri le parole che nascondono un tema.";
        case 'memoria':
          return "Abbina le coppie seguendo la connessione logica.";
        case 'ortografia':
          return "Crea le parole più lunghe possibile con le sette lettere date.";
        case 'cloze':
          return "Completa il testo con le parole mancanti.";
        case 'spettri':
          return "Ricostruisci le due progressioni.";
        case 'serpentoni':
          return "Trova tutte le parole del tema e lo strisciogramma.";
        default:
          return "Pronto per la sfida di oggi?";
      }
    case GAME_STATUS.IN_PROGRESS:
      switch (game) {
        case 'collegamenti':
          return "Continua a cercare i gruppi nascosti!";
        case 'scalinata':
          return "Hai ancora tentativi per trovare le parole!";
        case 'memoria':
          return "Continua a scoprire le coppie!";
        case 'ortografia':
          return "Continua a formare parole!";
        case 'cloze':
          return "Continua a riempire gli spazi!";
        case 'spettri':
          return "Continua a ordinare le progressioni!";
        case 'serpentoni':
          return "Continua a tracciare i percorsi nella griglia!";
        default:
          return "Continua così!";
      }
    case GAME_STATUS.FINISHED:
      switch (game) {
        case 'collegamenti':
          return "Perfetto! Hai trovato tutti i gruppi.";
        case 'scalinata':
          return "Eccellente! Hai completato tutti i livelli.";
        case 'memoria':
          return "Fantastico! Hai scoperto tutte le coppie.";
        case 'ortografia':
          return "Ottimo lavoro! Hai completato il gioco.";
        case 'cloze':
          return "Ottimo lavoro! Hai completato il testo.";
        case 'spettri':
          return "Perfetto! Hai ordinato tutte le progressioni.";
        case 'serpentoni':
          return "Ottimo! Hai completato il Serpentoni di oggi.";
        default:
          return "Congratulazioni!";
      }
    default:
      return "";
  }
};

// (moved) isWebShareSupported now lives in components/Share.js

export const getMostRecentDifficulty = (game) => {
  let mostRecentDate = null;
  let mostRecentDifficulty = null;
  
  // Check all localStorage keys for this game
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key.startsWith(`${game}_`)) {
      const datePart = key.split("_")[1]; // Extract the date part from the key
      const gameData = getLocalData(game, datePart);
      
      // Check if this game has a difficulty setting and has been played (has progress)
      if (gameData.difficulty && (gameData.score > 0 || gameData.submittedWords?.length > 0)) {
        const keyDate = new Date(datePart);
        
        // Update if this is the most recent date we've found
        if (!mostRecentDate || keyDate > mostRecentDate) {
          mostRecentDate = keyDate;
          mostRecentDifficulty = gameData.difficulty;
        }
      }
    }
  }
  
  return mostRecentDifficulty;
};

// Streak management functions
export const getStreakData = () => {
  const streakData = localStorage.getItem('streak_data');
  if (!streakData) {
    // Initialize streak data from existing game history for existing users
    return initializeStreakFromHistory();
  }
  
  const data = JSON.parse(streakData);
  const yesterday = new Date(`${getCurrentDate()}T12:00:00`);
  yesterday.setDate(yesterday.getDate() - 1);
  if (data.lastPlayedDate && data.lastPlayedDate < formatLocalDate(yesterday)
    && (data.currentStreak !== 0 || data.streakStartDate != null)) {
    data.currentStreak = 0;
    data.streakStartDate = null;
    setStreakData(data);
  }
  
  // Migration: Add completedDates if it doesn't exist
  if (!data.completedDates) {
    data.completedDates = {};
    
    // Populate completedDates from existing game data
    const games = Object.values(GAME_CONFIG).map(g => g.id);
    const today = new Date();
    
    // Check last 30 days for completed games
    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dateString = formatLocalDate(date);
      
      // Check if any game was completed on this date
      for (const game of games) {
        const gameData = getLocalData(game, dateString);
        if (gameData && Object.keys(gameData).length > 0) {
          // Check if game was actually completed (not just started)
          let isCompleted = false;
          switch (game) {
            case 'ortografia':
              isCompleted = gameData.score > 0 || gameData.submittedWords?.length > 0;
              break;
            case 'memoria':
              isCompleted = gameData.gameOver || gameData.matched?.length > 0;
              break;
            case 'collegamenti':
              isCompleted = gameData.gameOver || gameData.foundGroups?.length > 0;
              break;
            case 'scalinata':
              isCompleted = gameData.gameOver || Object.keys(gameData.guesses || {}).length > 0;
              break;
            case 'cloze':
              isCompleted = gameData.gameOver || gameData.completed;
              break;
            case 'spettri':
              isCompleted = gameData.gameOver || gameData.completed;
              break;
            case 'serpentoni':
              isCompleted = hasSerpentoniProgress(gameData);
              break;
            default:
              isCompleted = Object.keys(gameData).length > 0;
          }
          if (isCompleted) {
            data.completedDates[dateString] = true;
            break; // Found a completed game for this date, no need to check other games
          }
        }
      }
    }
    
    // Save the updated data
    setStreakData(data);
  }
  
  return data;
};

// Initialize streak data from existing game history (one-time for existing users)
const initializeStreakFromHistory = () => {
  const games = Object.values(GAME_CONFIG).map(g => g.id);
  const today = new Date();
  const completedDates = {};
  let lastPlayedDate = null;
  let longestStreak = 0;
  let currentStreak = 0;
  let streakStartDate = null;
  
  // Check last 365 days for completed games
  for (let i = 0; i < 365; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() - i);
    const dateString = formatLocalDate(date);
    
    // Check if any game was completed on this date
    let hasCompletedOnThisDate = false;
    for (const game of games) {
      const gameData = getLocalData(game, dateString);
      if (gameData && Object.keys(gameData).length > 0) {
        // Check if game was actually completed (not just started)
        let isCompleted = false;
        switch (game) {
          case 'ortografia':
            isCompleted = gameData.score > 0 || gameData.submittedWords?.length > 0;
            break;
          case 'memoria':
            isCompleted = gameData.gameOver || gameData.matched?.length > 0;
            break;
          case 'collegamenti':
            isCompleted = gameData.gameOver || gameData.foundGroups?.length > 0;
            break;
          case 'scalinata':
            isCompleted = gameData.gameOver || Object.keys(gameData.guesses || {}).length > 0;
            break;
          case 'cloze':
            isCompleted = gameData.gameOver || gameData.completed;
            break;
          case 'spettri':
            isCompleted = gameData.gameOver || gameData.completed;
            break;
          case 'serpentoni':
            isCompleted = hasSerpentoniProgress(gameData);
            break;
          default:
            isCompleted = Object.keys(gameData).length > 0;
        }
        if (isCompleted) {
          hasCompletedOnThisDate = true;
          break; // Found a completed game for this date, no need to check other games
        }
      }
    }
    
    if (hasCompletedOnThisDate) {
      completedDates[dateString] = true;
      
      // Track last played date
      if (!lastPlayedDate) {
        lastPlayedDate = dateString;
      }
    }
  }
  
  // Calculate current streak and longest streak
  if (lastPlayedDate) {
    const lastPlayed = new Date(`${lastPlayedDate}T12:00:00`);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    // If last played was today or yesterday, calculate current streak
    if (lastPlayedDate >= formatLocalDate(yesterday)) {
      let streakCount = 0;
      let currentDate = new Date(lastPlayed);
      
      // Count consecutive days backwards from last played date
      while (completedDates[formatLocalDate(currentDate)]) {
        streakCount++;
        currentDate.setDate(currentDate.getDate() - 1);
      }
      
      currentStreak = streakCount;
      streakStartDate = new Date(currentDate);
      streakStartDate.setDate(currentDate.getDate() + 1);
      streakStartDate = formatLocalDate(streakStartDate);
    }
    
    // Calculate longest streak
    let maxStreak = 0;
    let tempStreak = 0;
    const sortedDates = Object.keys(completedDates).sort();
    
    for (let i = 0; i < sortedDates.length; i++) {
      const currentDate = new Date(`${sortedDates[i]}T12:00:00`);
      const prevDate = i > 0 ? new Date(`${sortedDates[i - 1]}T12:00:00`) : null;
      const previousDay = new Date(currentDate);
      previousDay.setDate(previousDay.getDate() - 1);
      
      if (!prevDate || formatLocalDate(previousDay) === formatLocalDate(prevDate)) {
        // Consecutive day
        tempStreak++;
      } else {
        // Gap in streak
        maxStreak = Math.max(maxStreak, tempStreak);
        tempStreak = 1;
      }
    }
    
    // Check final streak
    maxStreak = Math.max(maxStreak, tempStreak);
    longestStreak = maxStreak;
  }
  
  const initialStreakData = {
    currentStreak,
    longestStreak,
    lastPlayedDate,
    streakStartDate,
    completedDates
  };
  
  // Save the initialized data
  setStreakData(initialStreakData);
  
  return initialStreakData;
};

export const setStreakData = (data) => {
  localStorage.setItem('streak_data', JSON.stringify(data));
};

export const hasCompletedGameToday = () => {
  const today = getCurrentDate();
  return hasCompletedGameOnDate(today);
};

// Check if a specific date was completed (using streak data)
export const hasCompletedGameOnDate = (date) => {
  const streakData = getStreakData();
  return streakData.completedDates && streakData.completedDates[date] === true;
};

// Get the current week's completion status (Sunday to Saturday)
export const getCurrentWeekCompletion = () => {
  const today = new Date();
  const todayMidnight = new Date(today);
  todayMidnight.setHours(0, 0, 0, 0);
  const currentDay = todayMidnight.getDay(); // 0 = Sunday, 1 = Monday, etc.
  
  // Calculate the start of the week (Sunday) in local time, midnight
  const startOfWeek = new Date(todayMidnight);
  startOfWeek.setDate(todayMidnight.getDate() - currentDay);
  
  const weekCompletion = [];
  
  for (let i = 0; i < 7; i++) {
    const date = new Date(startOfWeek);
    date.setDate(startOfWeek.getDate() + i);
    date.setHours(0, 0, 0, 0);
    const dateString = formatLocalDate(date);
    
    const isToday = dateString === getCurrentDate();
    const isCompleted = hasCompletedGameOnDate(dateString);
    const isPast = date < todayMidnight;
    
    weekCompletion.push({
      date: dateString,
      dayName: date.toLocaleDateString('it-IT', { weekday: 'short' }),
      isCompleted,
      isToday,
      isPast
    });
  }
  
  return weekCompletion;
};

// Build current week's (Sunday..Saturday) completion data from backend dates
// datesArray: array of YYYY-MM-DD strings returned by the backend for current week
export const buildCurrentWeekDataFromDates = (datesArray) => {
  if (!Array.isArray(datesArray)) return null;
  const completedDatesSet = new Set(
    datesArray
      .map((value) => String(value || '').trim())
      .map((value) => (value.includes('T') ? value.split('T')[0] : value))
      .filter(Boolean)
  );
  const today = new Date();
  const todayMidnight = new Date(today);
  todayMidnight.setHours(0, 0, 0, 0);
  const start = new Date(todayMidnight);
  // getDay(): 0=Sunday..6=Saturday; move to Sunday
  start.setDate(todayMidnight.getDate() - todayMidnight.getDay());
  const result = [];
  const todayStr = formatLocalDate(todayMidnight);
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    d.setHours(0, 0, 0, 0);
    const dateStr = formatLocalDate(d);
    result.push({
      date: dateStr,
      dayName: d.toLocaleDateString('it-IT', { weekday: 'short' }),
      isCompleted: completedDatesSet.has(dateStr),
      isToday: dateStr === todayStr,
      isPast: d < todayMidnight
    });
  }
  return result;
};

export const updateStreak = () => {
  const today = getCurrentDate();
  const streakData = getStreakData();
  
  // Check if user already played today
  if (streakData.lastPlayedDate === today) {
    return streakData; // No change needed
  }
  
  const yesterday = getPreviousDate(today);
  let newStreakData = { ...streakData };
  
  // Initialize completedDates if it doesn't exist (for backward compatibility)
  if (!newStreakData.completedDates) {
    newStreakData.completedDates = {};
  }
  
  // Mark today as completed
  newStreakData.completedDates[today] = true;
  
  if (streakData.lastPlayedDate === yesterday) {
    // Consecutive day - increment streak
    newStreakData.currentStreak += 1;
    newStreakData.lastPlayedDate = today;
    
    // Update longest streak if needed
    if (newStreakData.currentStreak > newStreakData.longestStreak) {
      newStreakData.longestStreak = newStreakData.currentStreak;
    }
  } else if (streakData.lastPlayedDate && streakData.lastPlayedDate !== today) {
    // Gap in streak - reset to 1
    newStreakData.currentStreak = 1;
    newStreakData.lastPlayedDate = today;
    newStreakData.streakStartDate = today;
    // Ensure longest streak is at least 1 when starting a new streak
    newStreakData.longestStreak = Math.max(newStreakData.longestStreak || 0, 1);
  } else {
    // First time playing or first time since initialization
    newStreakData.currentStreak = 1;
    newStreakData.lastPlayedDate = today;
    newStreakData.streakStartDate = today;
    // Ensure longest streak reflects today's first completion
    newStreakData.longestStreak = Math.max(newStreakData.longestStreak || 0, 1);
  }
  
  setStreakData(newStreakData);
  return newStreakData;
};

export const getDailyEncouragement = (streak) => {
  const encouragements = [
    "Non rompere la catena! Gioca oggi! ⛓️",
    "La tua fila continua! Mantienila viva! 💪",
    "Ogni giorno conta! Continua così! 🚀",
    "La costanza è la chiave! 🔑",
    "Un altro giorno, un altro successo! 🚀"
  ];
  
  if (streak === 0) {
    return "Inizia oggi la tua fila! 🔥";
  }
  
  return encouragements[streak % encouragements.length];
};

// Newsletter signup utilities
export const hasNewsletterSignupCompleted = () => {
  return localStorage.getItem('newsletter-signup-completed') !== null;
};

/**
 * Check if user has completed newsletter signup.
 * 
 * @deprecated Prefer using useUserProfile().isNewsletterSubscribed from UserProfileContext
 * This function is kept for backwards compatibility.
 * 
 * @param {boolean} isSignedIn - Whether the user is logged in
 * @param {boolean|null} isNewsletterSubscribedFromContext - Optional: subscription status from UserProfileContext
 * @returns {Promise<boolean>} - True if user has subscribed
 */
export const hasNewsletterSignupCompletedAsync = async (isSignedIn = false, isNewsletterSubscribedFromContext = null) => {
  // If we have the subscription status from context, use it (most reliable)
  if (isNewsletterSubscribedFromContext !== null) {
    // Sync localStorage with context state
    if (isNewsletterSubscribedFromContext && !localStorage.getItem('newsletter-signup-completed')) {
      localStorage.setItem('newsletter-signup-completed', Date.now().toString());
    } else if (!isNewsletterSubscribedFromContext && localStorage.getItem('newsletter-signup-completed')) {
      localStorage.removeItem('newsletter-signup-completed');
    }
    return isNewsletterSubscribedFromContext;
  }
  
  // Fallback: For logged-in users without context data, check API
  if (isSignedIn) {
    try {
      const { getUserProfile } = await import('./api/users');
      const profile = await getUserProfile();
      const apiSubscribed = !!profile?.newsletter_subscribed_at;
      
      // Sync localStorage with API state
      if (apiSubscribed && !localStorage.getItem('newsletter-signup-completed')) {
        localStorage.setItem('newsletter-signup-completed', Date.now().toString());
      } else if (!apiSubscribed && localStorage.getItem('newsletter-signup-completed')) {
        localStorage.removeItem('newsletter-signup-completed');
      }
      
      return apiSubscribed;
    } catch (error) {
      console.error('Error checking newsletter subscription from API:', error);
      return hasNewsletterSignupCompleted();
    }
  }
  
  // For non-logged-in users, use localStorage only
  return hasNewsletterSignupCompleted();
};

export const hasNewsletterSignupDismissed = () => {
  const dismissedTime = localStorage.getItem('newsletter-signup-dismissed');
  if (!dismissedTime) return false;
  
  const dismissedDate = new Date(parseInt(dismissedTime));
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  
  return dismissedDate > sevenDaysAgo;
};

export const hasCompletedAtLeastOneGame = () => {
  const streakData = getStreakData();
  console.log('streakData', streakData);
  return streakData && streakData.completedDates && Object.keys(streakData.completedDates).length > 0;
};

/**
 * Check if newsletter prompt should be shown.
 * @param {boolean|null} isNewsletterSubscribed - Optional: subscription status from UserProfileContext
 * @returns {boolean} - True if prompt should be shown
 */
export const shouldShowNewsletterPrompt = (isNewsletterSubscribed = null) => {
  // During Christmas lottery period, don't show regular newsletter prompt
  if (isInChristmasLotteryDateRange()) {
    return false;
  }
  
  // Don't show if already signed up (prefer context value if available)
  if (isNewsletterSubscribed === true) return false;
  if (isNewsletterSubscribed === null && hasNewsletterSignupCompleted()) return false;
  
  // Don't show if dismissed within last 7 days
  if (hasNewsletterSignupDismissed()) return false;
  
  // Don't show if user hasn't completed at least one game
  if (!hasCompletedAtLeastOneGame()) return false;
  
  return true;
};

export const markNewsletterSignupCompleted = () => {
  localStorage.setItem('newsletter-signup-completed', Date.now().toString());
};

export const markNewsletterSignupDismissed = () => {
  safeLocalStorageSetItem('newsletter-signup-dismissed', Date.now().toString());
};

// Christmas lottery utilities
export const isInChristmasLotteryDateRange = () => {
  const now = new Date();
  const startDate = new Date(2025, 11, 10); // December 10, 2025 (month is 0-indexed)
  const endDate = new Date(2025, 11, 23, 23, 59, 59); // December 23, 2025, end of day
  
  return now >= startDate && now <= endDate;
};

export const hasChristmasLotteryDismissed = () => {
  const dismissedTime = localStorage.getItem('christmas-lottery-dismissed');
  if (!dismissedTime) return false;
  
  const dismissedDate = new Date(parseInt(dismissedTime));
  const oneDayAgo = new Date();
  oneDayAgo.setDate(oneDayAgo.getDate() - 2);
  return dismissedDate > oneDayAgo;
};

export const markChristmasLotteryDismissed = () => {
  const now = new Date();
  safeLocalStorageSetItem('christmas-lottery-dismissed', now.getTime().toString());
};

export const shouldShowChristmasLotteryPrompt = async () => {
  // Only show during date range
  if (!isInChristmasLotteryDateRange()) return false;
  
  // Don't show if dismissed within last 48 hours
  if (hasChristmasLotteryDismissed()) return false;
  
  // Show to everyone (both subscribed and unsubscribed users)
  return true;
};
